/**
 * Composable for the DB-backed notification queue.
 * Replaces the in-memory useRewardNotification system with a persistent,
 * server-side queue consumed via Supabase Realtime + polling on app resume.
 */

import { ref, computed } from 'vue'
import { supabase } from '@/lib/supabase'
import { i18n } from '@/i18n'
import type { RealtimeChannel } from '@supabase/supabase-js'

export type NotificationType =
  | 'level_up'
  | 'chest_available'
  | 'reward_unlocked'
  | 'admin'
  | 'streak_milestone'
  | 'allowance_credited'
  | 'weekly_recap'
  | 'parent_cheer'
  // Synthetic types — only injected via pushFakeNotification for debug/QA.
  // The DB never emits these; they bypass the standard mapper and route
  // straight to the corresponding RewardNotification.type in
  // RewardNotificationQueue.
  | 'animal'
  | 'pose'
  | 'accessory'
  | 'background'
  | 'task-completion'

export interface DBNotification {
  id: string
  household_id: string
  child_id: string | null
  type: NotificationType
  priority: number
  payload: Record<string, any>
  action_type: 'dismiss' | 'action_required'
  action_reference_id: string | null
  dedup_key: string | null
  expires_at: string | null
  created_at: string
  /** 'child' : réservée à une vue enfant (voir setChildAudienceActive). */
  audience?: 'all' | 'child'
}

// ---------------------------------------------------------------------------
// Singleton state shared across all composable instances
// ---------------------------------------------------------------------------
const queue = ref<DBNotification[]>([])
const currentNotification = ref<DBNotification | null>(null)
const isProcessing = ref(false)

let channel: RealtimeChannel | null = null
let subscribedChildId: string | null = null
let subscribedHouseholdId: string | null = null

// Screenshot mode: suppress ALL notification modals (level-up, chest, admin…)
// so they don't pop over captures. The screenshot flow flips `allowOne` to true
// just before it deliberately triggers the reward celebration, so exactly that
// one notification is allowed through.
let screenshotSuppress = false
let screenshotAllowType: string | null = null

// Une célébration `audience = 'child'` (l'encouragement d'un parent) n'est
// montrée que dans une vue enfant : accès enfant, ou mode enfant sur l'appareil
// d'un parent. La file est partagée par tous les appareils du foyer et le
// premier qui ferme une célébration la supprime : le téléphone du parent la
// mangerait avant que l'enfant ne la voie.
let childAudienceActive = false

/** La vue courante est-elle celle d'un enfant ? Posé par RewardNotificationQueue. */
export function setChildAudienceActive(on: boolean): void {
  if (childAudienceActive === on) return
  childAudienceActive = on
  if (on) return
  // On sort de la vue enfant : ce qui lui était réservé quitte la file sans
  // être supprimé, l'enfant le retrouvera.
  queue.value = queue.value.filter(n => n.audience !== 'child')
  if (currentNotification.value?.audience === 'child') {
    currentNotification.value = null
    isProcessing.value = false
    processNext()
  }
}

/** Enable/disable global notification suppression (screenshot accounts). */
export function setNotificationSuppression(on: boolean): void {
  screenshotSuppress = on
  if (!on) screenshotAllowType = null
}

/**
 * Let the next notification of `type` through while suppression is on (e.g.
 * 'reward_unlocked' for the reward slide). Other types stay blocked, so stale
 * level_up/chest notifications pending in the DB don't take its place.
 */
export function allowNextNotification(type: string): void {
  screenshotAllowType = type
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
function sortQueue() {
  queue.value.sort((a, b) => {
    if (a.priority !== b.priority) return b.priority - a.priority
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  })
}

function processNext() {
  if (queue.value.length > 0) {
    isProcessing.value = true
    const next = queue.value.shift()!
    console.log('[NotificationQueue] Processing next:', { id: next.id, type: next.type, remaining: queue.value.length })
    currentNotification.value = next
  } else {
    console.log('[NotificationQueue] Queue empty')
    currentNotification.value = null
    isProcessing.value = false
  }
}

function addToQueue(notification: DBNotification) {
  if (notification.audience === 'child' && !childAudienceActive) return

  // Screenshot mode: block every notification modal unless its type was
  // explicitly allowed (the reward slide). Consumes the one-shot allowance.
  if (screenshotSuppress) {
    if (screenshotAllowType && notification.type === screenshotAllowType) {
      screenshotAllowType = null
    } else {
      return
    }
  }

  const isDuplicate =
    queue.value.some(n => n.id === notification.id) ||
    currentNotification.value?.id === notification.id
  if (isDuplicate) return

  queue.value.push(notification)
  sortQueue()

  if (!currentNotification.value && !isProcessing.value) {
    processNext()
  }
}

// ---------------------------------------------------------------------------
// Public composable
// ---------------------------------------------------------------------------
export function useNotificationQueue() {
  const t = (key: string, params?: Record<string, any>) => i18n.global.t(key, params)
  const locale = computed(() => i18n.global.locale.value as string)

  /**
   * Fetch all pending (non-expired) notifications for a child from the DB.
   * Used on mount and app resume to catch any that arrived while offline.
   */
  async function fetchPending(childId: string): Promise<void> {
    const { data, error } = await supabase
      .from('user_notifications')
      .select('*')
      .eq('child_id', childId)
      .or('expires_at.is.null,expires_at.gt.now()')
      .order('priority', { ascending: false })
      .order('created_at', { ascending: true })

    if (error) {
      console.error('[NotificationQueue] fetchPending error:', error)
      return
    }

    for (const row of (data ?? []) as unknown as DBNotification[]) {
      addToQueue(row)
    }
  }

  /**
   * Subscribe to Realtime INSERT/DELETE events for a child's notifications.
   */
  function subscribe(childId: string) {
    if (subscribedChildId === childId && channel) return
    unsubscribe()
    subscribedChildId = childId

    channel = supabase
      .channel(`notif-queue-${childId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_notifications',
        filter: `child_id=eq.${childId}`
      }, (payload) => {
        addToQueue(payload.new as unknown as DBNotification)
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'user_notifications',
        filter: `child_id=eq.${childId}`
      }, (payload) => {
        const deletedId = (payload.old as any)?.id
        if (!deletedId) return
        queue.value = queue.value.filter(n => n.id !== deletedId)
        if (currentNotification.value?.id === deletedId) {
          currentNotification.value = null
          isProcessing.value = false
          processNext()
        }
      })
      .subscribe()
  }

  function unsubscribe() {
    if (channel) {
      supabase.removeChannel(channel)
      channel = null
    }
    subscribedChildId = null
    subscribedHouseholdId = null
  }

  /**
   * Fetch all pending (non-expired) notifications for an ENTIRE household.
   * Used when no single child is active (all-kids page, parent dashboard) so a
   * parent still receives notifications for whichever child just earned a reward.
   */
  async function fetchPendingHousehold(householdId: string): Promise<void> {
    const { data, error } = await supabase
      .from('user_notifications')
      .select('*')
      .eq('household_id', householdId)
      .or('expires_at.is.null,expires_at.gt.now()')
      .order('priority', { ascending: false })
      .order('created_at', { ascending: true })

    if (error) {
      console.error('[NotificationQueue] fetchPendingHousehold error:', error)
      return
    }

    for (const row of (data ?? []) as unknown as DBNotification[]) {
      addToQueue(row)
    }
  }

  /**
   * Subscribe to Realtime INSERT/DELETE events for an entire household's
   * notifications (all children).
   */
  function subscribeHousehold(householdId: string) {
    if (subscribedHouseholdId === householdId && channel) return
    unsubscribe()
    subscribedHouseholdId = householdId

    channel = supabase
      .channel(`notif-queue-household-${householdId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_notifications',
        filter: `household_id=eq.${householdId}`
      }, (payload) => {
        addToQueue(payload.new as unknown as DBNotification)
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'user_notifications',
        filter: `household_id=eq.${householdId}`
      }, (payload) => {
        const deletedId = (payload.old as any)?.id
        if (!deletedId) return
        queue.value = queue.value.filter(n => n.id !== deletedId)
        if (currentNotification.value?.id === deletedId) {
          currentNotification.value = null
          isProcessing.value = false
          processNext()
        }
      })
      .subscribe()
  }

  /**
   * Switch to household-wide listening: clear state, re-subscribe at household
   * level, fetch pending household notifications. Use when no single child is
   * active.
   */
  function switchHousehold(householdId: string) {
    // Already listening to this household → don't wipe the in-progress queue.
    if (subscribedHouseholdId === householdId && channel) return
    queue.value = []
    currentNotification.value = null
    isProcessing.value = false
    subscribeHousehold(householdId)
    fetchPendingHousehold(householdId)
  }

  /**
   * Dismiss the current notification (for `action_type = 'dismiss'`).
   * Deletes the row from the DB then moves to the next in queue.
   */
  async function dismissCurrent(): Promise<void> {
    if (!currentNotification.value) return

    const id = currentNotification.value.id
    currentNotification.value = null
    isProcessing.value = false

    try {
      await supabase.from('user_notifications').delete().eq('id', id)
    } catch (err) {
      console.error('[NotificationQueue] Error deleting notification:', err)
    }

    setTimeout(() => processNext(), 500)
  }

  /**
   * Passe la notification courante SANS la supprimer en base : elle reviendra
   * au prochain `fetchPending`. Pour les cas où l'on n'a pas pu la traiter
   * (lecture du coffre en échec réseau), par opposition à `dismissCurrent`, qui
   * acte que l'utilisateur l'a vue. Supprimer sur une lecture ratée revenait à
   * jeter la notification à cause d'une coupure.
   */
  function skipCurrent(): void {
    if (!currentNotification.value) return
    currentNotification.value = null
    isProcessing.value = false
    setTimeout(() => processNext(), 500)
  }

  /**
   * Écarte d'un coup toutes les célébrations d'un type : la courante et celles
   * qui attendent. Pour « Plus tard » sur un coffre : quinze coffres en
   * attente faisaient quinze plein écran d'affilée avant d'atteindre l'app.
   * Les coffres eux-mêmes restent dans le stock (onglet Bonus), seules leurs
   * annonces partent.
   */
  async function dismissAllOfType(type: NotificationType): Promise<void> {
    const ids = queue.value.filter(n => n.type === type).map(n => n.id)
    if (currentNotification.value?.type === type) {
      ids.push(currentNotification.value.id)
      currentNotification.value = null
      isProcessing.value = false
    }
    queue.value = queue.value.filter(n => n.type !== type)

    const stored = ids.filter(id => !id.startsWith('fake-'))
    if (stored.length) {
      try {
        await supabase.from('user_notifications').delete().in('id', stored)
      } catch (err) {
        console.error('[NotificationQueue] Error dismissing notifications:', err)
      }
    }

    setTimeout(() => processNext(), 500)
  }

  /**
   * Mark an action-required notification as completed (e.g. chest claimed).
   * Deletes the row from the DB then moves to the next in queue.
   */
  async function completeAction(notificationId: string): Promise<void> {
    try {
      await supabase.from('user_notifications').delete().eq('id', notificationId)
    } catch (err) {
      console.error('[NotificationQueue] Error completing action:', err)
    }

    if (currentNotification.value?.id === notificationId) {
      currentNotification.value = null
      isProcessing.value = false
      setTimeout(() => processNext(), 500)
    }
  }

  /**
   * Switch to a different child: clear state, re-subscribe, fetch pending.
   */
  function switchChild(childId: string) {
    queue.value = []
    currentNotification.value = null
    isProcessing.value = false
    subscribe(childId)
    fetchPending(childId)
  }

  /**
   * Full teardown — call when user logs out.
   */
  function clearAll() {
    queue.value = []
    currentNotification.value = null
    isProcessing.value = false
    unsubscribe()
  }

  // -----------------------------------------------------------------------
  // Helpers to map DB notification payloads into display-ready data
  // -----------------------------------------------------------------------

  function getDisplayMessage(notification: DBNotification): string {
    switch (notification.type) {
      case 'level_up':
        return t('notifications.messages.level', { level: notification.payload.level })
      case 'reward_unlocked':
        return t('notifications.messages.reward')
      case 'chest_available':
        return t('notifications.messages.chest')
      case 'weekly_recap':
        return t('weeklyRecap.celebration.parentTitle', { name: notification.payload.child_name ?? '' })
      case 'admin':
        return notification.payload.message ?? notification.payload.title ?? ''
      case 'allowance_credited': {
        const amount = Number(notification.payload.amount ?? 0)
        const currency = notification.payload.currency ?? 'EUR'
        const formatted = new Intl.NumberFormat(locale.value, { style: 'currency', currency }).format(amount)
        return t('allowance.notif.credited', { amount: formatted })
      }
      default:
        return ''
    }
  }

  /**
   * Inject a synthetic notification into the queue without touching the DB.
   * Intended for debug/QA in development only — not called from production code.
   */
  function pushFakeNotification(
    type: NotificationType,
    payload: Record<string, any> = {},
    childId: string | null = null,
  ): void {
    const fake: DBNotification = {
      id: `fake-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      household_id: '',
      child_id: childId,
      type,
      priority: 0,
      payload,
      action_type: 'dismiss',
      action_reference_id: null,
      dedup_key: null,
      expires_at: null,
      created_at: new Date().toISOString(),
    }
    addToQueue(fake)
  }

  return {
    queue: computed(() => queue.value),
    currentNotification: computed(() => currentNotification.value),
    isProcessing: computed(() => isProcessing.value),
    pendingCount: computed(() => queue.value.length),

    fetchPending,
    fetchPendingHousehold,
    subscribe,
    subscribeHousehold,
    switchHousehold,
    unsubscribe,
    dismissCurrent,
    dismissAllOfType,
    skipCurrent,
    completeAction,
    switchChild,
    clearAll,
    processNext,
    getDisplayMessage,
    pushFakeNotification,
  }
}
